import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays, Users, StickyNote, Eye, EyeOff, Plus, Trash2, ArrowLeftRight, Pencil, Hotel, GitBranch, WifiOff, Images, PlaneTakeoff, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import ShareBar from "../components/travel/ShareBar";
import TripDocuments from "../components/travel/TripDocuments";
import TripFiles from "../components/travel/TripFiles";
import OfflineReadyCard from "../components/trip/OfflineReadyCard";
import ReadinessTile from "../components/trip/ReadinessTile";
import FlightsRollup from "../components/trip/FlightsRollup";
import PrepTemplatePicker from "../components/trip/PrepTemplatePicker";
import TripTasksWidget from "../components/trip/TripTasksWidget";
import TripSharing from "../components/travel/TripSharing";
import TripPacking from "../components/travel/TripPacking";
import TripExpenses from "../components/travel/TripExpenses";
import TripGuideLinks from "../components/travel/TripGuideLinks";
import TripSegments from "../components/travel/TripSegments";
import TripCarbon from "../components/travel/TripCarbon";
import JourneyChecklist from "../components/travel/JourneyChecklist";
import ForgettingScan from "../components/travel/ForgettingScan";
import TripOverview from "../components/travel/TripOverview";
import TripJournal from "../components/travel/TripJournal";
import ReminderDialog from "../components/travel/ReminderDialog";
import TripCompletion from "../components/travel/TripCompletion";
import EmergencyCard from "../components/travel/EmergencyCard";
import AskMyTrip from "../components/travel/AskMyTrip";
import TripAlbum from "../components/travel/TripAlbum";
import BagsForTrip from "../components/wallet/BagsForTrip";
import TripChatCard from "../components/diary/TripChatCard";
import TripPolls from "../components/travel/TripPolls";
import VisaEntry from "../components/travel/VisaEntry";
import { TripEditDialog } from "../components/travel/TripEditDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import TripWeather from "../components/travel/TripWeather";
import TripMap from "../components/travel/TripMap";
import DayPlanBoard from "../components/travel/DayPlanBoard";
import OfflineTripKit from "../components/travel/OfflineTripKit";
import SideTrips from "../components/travel/SideTrips";
import ReadinessPanel from "../components/travel/ReadinessPanel";
import TripBenefits from "../components/travel/TripBenefits";
import SendCopyButton from "../components/trip/SendCopyButton";
import { BookOpenText } from "lucide-react";

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [trip, setTrip] = useState(null);
  const [showReminder, setShowReminder] = useState(false);
  const [err, setErr] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [allTravelers, setAllTravelers] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [returnBusy, setReturnBusy] = useState(false);
  const [editingTrip, setEditingTrip] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'delete' | 'return'
  const [offlineAt, setOfflineAt] = useState(null); // showing a cached offline copy

  // Legacy trips may hold object stops ({city, country}) — normalize to strings so rendering never crashes
  const normalizeTrip = (t) => ({
    ...t,
    stops: (t.stops || []).map((s) => (typeof s === "string" ? s : [s?.city, s?.country].filter(Boolean).join(", "))),
  });

  const load = useCallback(() => {
    api.get(`/trips/${id}`).then((r) => {
      setTrip(normalizeTrip(r.data)); setOfflineAt(null);
      try { // Offline Travel Mode: keep the essentials cached locally
        localStorage.setItem(`tp_offline_${id}`, JSON.stringify({ trip: r.data, saved_at: new Date().toISOString() }));
      } catch { /* storage full — offline copy skipped */ }
    }).catch(() => {
      try {
        const c = JSON.parse(localStorage.getItem(`tp_offline_${id}`) || "null");
        if (c?.trip) { setTrip(normalizeTrip(c.trip)); setOfflineAt(c.saved_at); return; }
      } catch { /* no cached copy */ }
      setErr(true);
    });
  }, [id]);
  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {  // deep-link support: /trips/:id#photos scrolls straight to the Photos area
    if (trip && window.location.hash === "#photos") {
      setTimeout(() => document.getElementById("photos")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
    if (trip && window.location.hash === "#expenses") {
      setTimeout(() => document.getElementById("expenses")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [trip?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) api.get("/trips/travelers").then((r) => setAllTravelers(r.data.items)).catch(() => {}); }, [user]);

  if (authLoading) return <Spinner full />;
  if (!user) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Please sign in to view this trip. <Link to="/travel-hub" className="text-primary hover:underline">My Travel Hub</Link></div>;
  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Trip not found.</div>;
  if (!trip) return <Spinner full />;
  // Content-edit rights: owner + edit only. "finance" manages money but is read-only
  // for the itinerary, packing, journal etc. (matches backend guards).
  const canEditContent = !trip.my_role || ["owner", "edit"].includes(trip.my_role);

  const reveal = async (tid) => {
    if (revealed[tid]) { setRevealed((r) => ({ ...r, [tid]: null })); return; }
    try { const { data } = await api.get(`/trips/travelers/${tid}/reveal`); setRevealed((r) => ({ ...r, [tid]: data })); }
    catch { toast.error("Could not reveal traveller documents"); }
  };

  const attach = async (tid) => {
    const ids = Array.from(new Set([...(trip.traveler_ids || []), tid]));
    try { await api.post("/trips", { id: trip.id, title: trip.title, traveler_ids: ids, stops: trip.stops, start_date: trip.start_date, end_date: trip.end_date, description: trip.description }); setAttaching(false); load(); toast.success("Traveller added to trip"); }
    catch { toast.error("Could not add traveller"); }
  };
  const detach = async (tid) => {
    const ids = (trip.traveler_ids || []).filter((x) => x !== tid);
    try { await api.post("/trips", { id: trip.id, title: trip.title, traveler_ids: ids, stops: trip.stops, start_date: trip.start_date, end_date: trip.end_date, description: trip.description }); load(); }
    catch { toast.error("Could not remove traveller from trip"); }
  };

  const setCover = async (aid) => {
    try {
      await api.post("/trips", { id: trip.id, title: trip.title, cover: aid });
      toast.success(aid ? "Cover photo set — it now shows on your Travel Hub trip card" : "Cover photo removed");
      load();
    } catch { toast.error("Could not update cover photo"); }
  };

  const createReturnJourney = async () => {
    setReturnBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/return-journey`, {});
      toast.success("Return journey created — review the mirrored legs and add your booking details");
      setConfirming(null);
      navigate(`/trips/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the return journey"); }
    setReturnBusy(false);
  };

  const deleteTrip = async () => {
    try {
      await api.delete(`/trips/${trip.id}`);
      toast.success("Moved to Recently Deleted");
      navigate("/travel-hub");
    } catch { toast.error("Could not delete trip"); }
  };

  const toggleArchive = async () => {
    const archiving = trip.lifecycle !== "archived";
    try {
      await api.post(`/trips/${trip.id}/archive`, { archive: archiving });
      toast.success(archiving ? "Trip archived — everything is preserved" : "Restored to active");
      load();
    } catch { toast.error("Failed"); }
  };

  const downloadIcs = async () => {
    try {
      const res = await api.get(`/trips/${trip.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(trip.title || "trip").replace(/\s+/g, "_")}.ics`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded — open it to add the trip to your calendar");
    } catch { toast.error("Could not export the calendar file"); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/trips/${trip.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(trip.title || "trip").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Itinerary PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setPdfBusy(false); }
  };

  const shareTripPdf = async () => {
    setShareBusy(true);
    try {
      const res = await api.get(`/trips/${trip.id}/pdf`, { responseType: "blob" });
      const file = new File([res.data], `${(trip.title || "trip").replace(/\s+/g, "_")}.pdf`, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: trip.title, text: "My trip itinerary from TravelerPulse.pro" });
      } else {
        await navigator.share({ title: trip.title, text: tripSummaryText() });
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing not available here — use the PDF download instead");
    } finally { setShareBusy(false); }
  };

  const tripSummaryText = () => {
    const lines = [(trip.start_date || "") + (trip.end_date ? ` – ${trip.end_date}` : "")];
    if (trip.stops?.length) lines.push(trip.stops.join(" → "));
    (trip.segments || []).slice(0, 8).forEach((s) => {
      lines.push([s.date, s.time?.slice(0, 5), (s.mode || "").toUpperCase(), s.ref,
        [s.from_place, s.to_place].filter(Boolean).join("→"),
        s.seat && `Seat ${s.seat}`, s.booking_ref && `PNR ${s.booking_ref}`].filter(Boolean).join(" "));
    });
    return lines.filter(Boolean).join("\n");
  };

  const dates = [trip.start_date, trip.end_date].filter(Boolean).join(" – ");
  const linkedIds = trip.traveler_ids || [];
  const unlinked = allTravelers.filter((t) => !linkedIds.includes(t.id));
  // hotel destination: last stop, unless it's the round-trip origin → use the middle stop
  const stops = trip.stops || [];
  const hotelDest = stops.length > 1 && stops[stops.length - 1] === stops[0]
    ? stops[Math.floor(stops.length / 2)] : stops[stops.length - 1];

  return (
    <div data-testid="trip-detail" className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4"><ArrowLeft className="w-4 h-4" /> My Travel Hub</Link>

      {trip.cover && (
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/trips/attachments/${trip.cover}/image?auth=${localStorage.getItem("token")}`}
          alt={`${trip.title} cover`} data-testid="trip-cover-hero"
          className="w-full h-44 sm:h-60 object-cover border border-zinc-300 mb-6"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}

      <div className="border-b border-zinc-200 pb-6 mb-8">
        {offlineAt && (
          <div className="mb-3 border border-amber-400 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-center gap-2" data-testid="offline-copy-banner">
            <WifiOff className="w-4 h-4 shrink-0" />
            <span><b>Offline copy</b> · last updated {new Date(offlineAt).toLocaleString()} — timeline, confirmations &amp; addresses are available; editing needs internet.</span>
          </div>
        )}
        {trip.parent && (
          <Link to={`/trips/${trip.parent.id}`} data-testid="trip-parent-link"
            className="inline-flex items-center gap-1.5 text-xs font-700 bg-violet-50 border border-violet-300 text-violet-800 px-2.5 py-1.5 mb-3 hover:bg-violet-100 transition-colors">
            <GitBranch className="w-3.5 h-3.5" /> Side trip of: {trip.parent.title}
          </Link>
        )}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3 flex-wrap">{trip.title}
            {trip.lifecycle === "archived" && <span className="text-xs font-900 tracking-widest bg-zinc-200 text-zinc-600 px-2 py-1 align-middle" data-testid="trip-archived-badge">ARCHIVED</span>}
          </h1>
          {(!trip.my_role || trip.my_role === "owner") && (
            <div className="flex gap-2 flex-wrap sm:shrink-0 sm:justify-end">
              <EmergencyCard tripId={trip.id} />
              <button onClick={() => setEditingTrip(true)} data-testid="trip-edit-btn"
                title="Edit title, dates, stops & description"
                className="inline-flex items-center gap-1.5 border border-blue-300 text-blue-700 px-3 py-2 text-sm font-700 hover:bg-blue-50 transition-colors">
                <Pencil className="w-4 h-4" /> Edit
              </button>
              <button onClick={() => setConfirming("return")} disabled={returnBusy} data-testid="trip-return-journey-btn"
                title="1-tap return journey — mirrors this trip in reverse"
                className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-700 px-3 py-2 text-sm font-700 hover:bg-emerald-50 transition-colors disabled:opacity-50">
                <ArrowLeftRight className="w-4 h-4" /> {returnBusy ? "Creating…" : "Return trip"}
              </button>
              <button onClick={() => setShowReminder(true)} data-testid="trip-set-reminder-btn"
                className="inline-flex items-center gap-1.5 border border-indigo-300 text-indigo-700 px-3 py-2 text-sm font-700 hover:bg-indigo-50 transition-colors">
                🔔 Reminder
              </button>
              <button onClick={toggleArchive} data-testid="trip-archive-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-700 px-3 py-2 text-sm font-700 hover:bg-zinc-50 transition-colors">
                {trip.lifecycle === "archived" ? "Restore to Active" : "Archive"}
              </button>
              <button onClick={() => setConfirming("delete")} data-testid="trip-delete-btn" title="Move to Recently Deleted"
                className="inline-flex items-center gap-1.5 border border-rose-300 text-rose-600 px-3 py-2 text-sm font-700 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          )}
        </div>
        {trip.my_role && trip.my_role !== "owner" && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-300 text-sky-800 text-xs font-700 px-2 py-1" data-testid="trip-shared-badge">
              Shared by {trip.owner_name || "a member"} · you can {trip.my_role === "edit" ? "view & edit" : trip.my_role === "finance" ? "view & manage expenses" : "view"}
            </div>
            {trip.my_role === "edit" && (
              <button onClick={() => setEditingTrip(true)} data-testid="trip-edit-btn-editor"
                className="inline-flex items-center gap-1 border border-blue-300 text-blue-700 px-2 py-1 text-xs font-700 hover:bg-blue-50">
                <Pencil className="w-3.5 h-3.5" /> Edit trip
              </button>
            )}
          </div>
        )}
        {dates && <div className="mt-2 flex items-center gap-1.5 text-muted-foreground"><CalendarDays className="w-4 h-4" /> {dates}</div>}
        {trip.stops?.length > 0 && <div className="mt-1 flex items-center gap-1.5 text-sm text-zinc-700"><MapPin className="w-4 h-4 text-primary" /> {trip.stops.join(" → ")}</div>}
        {trip.side_trips?.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="trip-side-trips-header">
            <span className="inline-flex items-center gap-1 text-xs font-700 text-violet-700">
              <GitBranch className="w-3.5 h-3.5" /> Side trips of this journey:
            </span>
            {trip.side_trips.map((s) => (
              <Link key={s.id} to={`/trips/${s.id}`} data-testid={`trip-header-side-chip-${s.id}`}
                className="text-xs font-700 text-violet-800 bg-violet-50 border border-violet-300 px-2 py-0.5 hover:border-violet-500 transition-colors">
                {s.title}
              </Link>
            ))}
          </div>
        )}
        {hotelDest && (
          <a href={`/hotels?place=${encodeURIComponent(hotelDest)}&checkin=${trip.start_date || ""}&checkout=${trip.end_date || ""}&trip=${trip.id}&campaign=TP-MYTRAVELHUB`}
            data-testid="trip-find-hotels"
            className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-teal-700 font-700 hover:underline">
            <Hotel className="w-4 h-4" /> Find hotels in {hotelDest}
          </a>
        )}
        {trip.description && <p className="mt-3 text-base text-zinc-700">{trip.description}</p>}
        <TripGuideLinks stops={stops} />
        <div className="mt-5">
          <ShareBar title={trip.title} text={tripSummaryText()} onPdf={downloadPdf} pdfBusy={pdfBusy}
            onShareFile={shareTripPdf} shareBusy={shareBusy} />
          <button onClick={downloadIcs} data-testid="trip-ics-btn"
            className="mt-2 inline-flex items-center gap-1.5 border border-blue-300 text-blue-700 px-3 py-2 text-sm font-700 hover:bg-blue-50 transition-colors">
            <CalendarDays className="w-4 h-4" /> Add to calendar (.ics)
          </button>
          <button onClick={() => document.getElementById("photos")?.scrollIntoView({ behavior: "smooth" })} data-testid="trip-photos-jump"
            className="mt-2 ml-2 inline-flex items-center gap-1.5 border border-emerald-400 bg-emerald-50 text-emerald-700 px-3 py-2 text-sm font-700 hover:bg-emerald-100 transition-colors">
            <Images className="w-4 h-4" /> Photos
          </button>
          <Link to={`/travel-day/${trip.id}`} data-testid="trip-travel-day-btn"
            className="mt-2 ml-2 inline-flex items-center gap-1.5 border border-amber-400 bg-amber-50 text-amber-700 px-3 py-2 text-sm font-700 hover:bg-amber-100 transition-colors">
            <PlaneTakeoff className="w-4 h-4" /> Travel Day
          </Link>
          <SendCopyButton tripId={trip.id} tripTitle={trip.title} />
          <ForgettingScan trip={trip} />
          <p className="mt-2 text-xs text-muted-foreground">Download the PDF for offline access, or "Send PDF to apps" to save it to WhatsApp, Files, Google Drive or Docs. The .ics file adds every leg to Google / Apple / Outlook Calendar. Exports include your timeline, travellers &amp; PNRs but never passport numbers, DOB, TSA or CLEAR details.</p>
        </div>
      </div>

      <TripCompletion tripId={trip.id} refreshKey={trip} />

      {/* Offline Travel Mode — pre-download this trip for no-internet use */}
      <OfflineTripKit trip={trip} />

      {(trip.shared_with?.length > 0 || ["household", "selected"].includes(trip.privacy) || (trip.my_role && trip.my_role !== "owner")) && (
        <>
          <TripChatCard tripId={trip.id} isOwner={!trip.my_role || trip.my_role === "owner"} />
          <TripPolls tripId={trip.id} canCreate={!trip.my_role || ["owner", "edit"].includes(trip.my_role)}
            isOwner={!trip.my_role || trip.my_role === "owner"} />
        </>
      )}

      <ReadinessPanel trip={trip} />

      <Link to={`/trips/${trip.id}/diary`} data-testid="open-diary-btn"
        className="mb-10 flex items-center gap-3 border border-indigo-300 bg-indigo-50/60 px-4 py-3 hover:border-indigo-500 transition-colors">
        <BookOpenText className="w-6 h-6 text-indigo-600 shrink-0" />
        <div>
          <div className="font-heading font-900 text-indigo-800">Traveler Diary</div>
          <div className="text-xs text-muted-foreground">Plan each day, keep a daily journal, track tasks, places & spending — then export it all as a PDF for offline use.</div>
        </div>
      </Link>

      <TripBenefits trip={trip} />

      <TripWeather trip={trip} />

      <TripMap trip={trip} />

      {/* Travellers */}
      <section className="mb-10" data-testid="trip-travelers">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-900 text-blue-600 text-2xl flex items-center gap-2"><Users className="w-6 h-6" /> Travellers on this trip</h2>
          {unlinked.length > 0 && <button onClick={() => setAttaching((a) => !a)} data-testid="trip-attach-traveler" className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add traveller</button>}
        </div>
        {attaching && (
          <div className="border border-primary bg-blue-50/40 p-3 mb-4 flex flex-wrap gap-2" data-testid="trip-attach-list">
            {unlinked.map((t) => (
              <button key={t.id} onClick={() => attach(t.id)} className="px-3 py-1.5 border border-zinc-300 bg-white text-sm hover:border-primary">{t.name}{t.relation ? ` · ${t.relation}` : ""}</button>
            ))}
          </div>
        )}
        {trip.travelers?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {trip.travelers.map((t) => {
              const rv = revealed[t.id];
              return (
                <div key={t.id} className="border border-zinc-300 bg-white p-4" data-testid={`trip-traveler-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div><div className="font-heading font-900">{t.name}</div>{t.relation && <div className="text-xs text-muted-foreground">{t.relation}</div>}</div>
                    <button onClick={() => detach(t.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {t.phone && <a href={`https://wa.me/${t.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid={`trip-traveler-whatsapp-${t.id}`} className="text-xs font-mono mt-2 block text-emerald-700 hover:underline">WhatsApp: {t.phone}</a>}
                  {t.pnr && <div className="text-xs font-mono mt-2">PNR: <span className="text-zinc-900">{t.pnr}</span></div>}
                  {t.notes && <div className="text-sm text-zinc-600 mt-1">{t.notes}</div>}
                  {t.has_sensitive && (
                    <div className="mt-2 border-t border-zinc-100 pt-2 text-xs font-mono space-y-0.5">
                      <button onClick={() => reveal(t.id)} data-testid={`trip-traveler-reveal-${t.id}`} className="text-primary inline-flex items-center gap-1 mb-1">{rv ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}{rv ? "Hide" : "Reveal"} documents</button>
                      <div>Passport: {(rv || t).passport_no || "—"}</div>
                      <div>TSA Pre: {(rv || t).tsa_pre || "—"} · CLEAR: {(rv || t).clear || "—"}</div>
                      {(rv || t).dob && <div>DOB: {(rv || t).dob}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-muted-foreground">No travellers linked yet. Add travellers you saved in your Travel Hub.</p>}
      </section>

      {/* Sharing (owner only) */}
      {(!trip.my_role || trip.my_role === "owner") && <TripSharing trip={trip} onChange={load} />}

      {showReminder && (
        <ReminderDialog entityType="trip" entityId={trip.id} tripId={trip.id}
          defaultTitle={trip.title} refDate={trip.start_date}
          onClose={() => setShowReminder(false)} onSaved={() => {}} />
      )}

      {editingTrip && (
        <TripEditDialog trip={trip} onClose={() => setEditingTrip(false)} onSaved={load} />
      )}

      <ConfirmDialog open={confirming === "delete"} tone="danger" title="Move trip to Recently Deleted?"
        message={`"${trip.title}" will move to Recently Deleted and stays recoverable for 30 days from My Travel Hub.`}
        confirmLabel="Delete trip" testid="confirm-delete-trip"
        onConfirm={deleteTrip} onCancel={() => setConfirming(null)} />

      <ConfirmDialog open={confirming === "return"} title="Create a return journey?"
        message={`We'll create a new trip mirroring "${trip.title}" in reverse — stops and legs swapped, ready for you to add flight numbers, dates and times once booked.`}
        confirmLabel="Create return trip" busy={returnBusy} testid="confirm-return-journey"
        onConfirm={createReturnJourney} onCancel={() => setConfirming(null)} />

      {/* Master readiness dashboard tile (Spec §9) — lifecycle + circle + offline + next action */}
      <ReadinessTile tripId={trip.id} />

      {/* Flight rollup (collapsible) — groups every flight segment as one journey */}
      <FlightsRollup segments={trip.segments || []} />

      {/* One-tap Prep Template picker (Spec §10) */}
      <PrepTemplatePicker tripId={trip.id} onApplied={load} />

      {/* Applied preparation tasks (Spec §10) */}
      <TripTasksWidget tripId={trip.id} />

      {/* Offline Travel Pack (Spec §15) — download for on-plane / no-network use */}
      <OfflineReadyCard tripId={trip.id} tripTitle={trip.title} />

      {/* Documents & Photos vault (owner only — files stay private) */}
      {(!trip.my_role || trip.my_role === "owner") && (
        <TripFiles ownerType="trip" ownerId={trip.id} items={trip.attachments || []} onChange={load}
          coverId={trip.cover || null} onSetCover={setCover} />
      )}

      {/* Travel documents checklist */}
      <VisaEntry trip={trip} />

      <TripDocuments tripId={trip.id} />

      {/* Day-by-day itinerary (AI planned or manual) — drag & drop editing */}
      <DayPlanBoard key={JSON.stringify(trip.day_plan || []).length} trip={trip} readOnly={!canEditContent} />

      {/* Itinerary segments */}
      <TripOverview tripId={trip.id} refreshKey={trip} />

      <TripSegments trip={trip} readOnly={!canEditContent} />

      <JourneyChecklist trip={trip} readOnly={!canEditContent} />

      <TripCarbon trip={trip} />

      <SideTrips trip={trip} readOnly={!canEditContent} />

      <AskMyTrip tripId={trip.id} />

      <TripAlbum trip={trip} />

      {/* Bag Feed On Trip Day — read-only manifest of every traveller's bags */}
      <BagsForTrip tripId={trip.id} tripTitle={trip.title} />

      {/* Packing list */}
      <TripPacking tripId={trip.id} tripStartDate={trip.start_date} readOnly={!canEditContent}
        people={(trip.travelers || []).map((t) => t.name).filter(Boolean)}
        travelers={trip.travelers || []} />

      {(!trip.my_role || ["owner", "edit", "finance"].includes(trip.my_role)) ? (
        <TripExpenses tripId={trip.id} tripTitle={trip.title} readOnly={false}
          isOwner={!trip.my_role || trip.my_role === "owner"}
        people={(trip.travelers || []).map((t) => ({ name: t.name, phone: t.phone })).filter((p) => p.name)} />
      ) : (
        <section id="expenses" className="mb-10 scroll-mt-24 border border-zinc-300 bg-zinc-50 p-5 flex items-start gap-3" data-testid="trip-expenses-locked">
          <Lock className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-heading font-700">Trip expenses are private</div>
            <p className="text-sm text-muted-foreground mt-1">Money on this trip is visible only to the organizer, editors and financial managers. Ask {trip.owner_name || "the trip owner"} for financial-manager access if you need it.</p>
          </div>
        </section>
      )}
      {/* Day-by-day photo journal */}
      <TripJournal tripId={trip.id} tripStartDate={trip.start_date} readOnly={!canEditContent} />

      {/* Notes */}
      <section data-testid="trip-notes">
        <h2 className="font-heading font-900 text-violet-600 text-2xl flex items-center gap-2 mb-4"><StickyNote className="w-6 h-6" /> Trip notes &amp; flights</h2>
        {trip.notes?.length ? (
          <div className="space-y-2">
            {trip.notes.map((n) => (
              <div key={n.id} className="border border-zinc-300 bg-white p-4" data-testid={`trip-note-${n.id}`}>
                <div className="font-600 text-sm">{n.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {[n.trip_date, `${n.airline || ""} ${n.flight || ""}`.trim(), (n.dep || n.arr) ? `${n.dep || "?"} → ${n.arr || "?"}` : ""].filter(Boolean).join(" · ")}
                </div>
                {n.notes && <div className="text-sm text-zinc-700 mt-1">{n.notes}</div>}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">No notes linked to this trip yet.</p>}
      </section>
    </div>
  );
}
